import apiService from './apiService';

export interface VeritralGame {
  id: string;
  game_id: string;
  name: string;
  provider: string;
  provider_name?: string;
  type: string;
  category?: string;
  image?: string;
  thumbnail?: string;
  rtp?: number;
  is_new?: boolean;
  is_featured?: boolean;
  is_popular?: boolean;
}

export interface VeritralProvider {
  code: string;
  name: string;
  logo?: string;
  game_count?: number;
}

export interface GetGamesParams {
  provider?: string;
  type?: string;
  page?: number;
  limit?: number;
  language?: string;
}

export interface GetGamesResponse {
  status: number;
  games: VeritralGame[];
  total: number;
  providers: VeritralProvider[];
  message?: { success?: string; warning?: string };
}

export interface LaunchGameResponse {
  status: number;
  result?: {
    link: string;
    sessionId?: string;
  };
  message?: { success?: string; warning?: string };
}

/**
 * Get games from Veritral aggregator
 */
export const getVeritralGames = async (params: GetGamesParams = {}): Promise<GetGamesResponse> => {
  const response = await apiService.postRaw<GetGamesResponse>('/api/veritral/games', params);
  return response;
};

/**
 * Get providers from Veritral
 */
export const getVeritralProviders = async (): Promise<{ status: number; providers: VeritralProvider[] }> => {
  const response = await apiService.postRaw<{ status: number; providers: VeritralProvider[] }>('/api/veritral/providers', {});
  return response;
};

/**
 * Launch a real-money game via backend (POST /api/veritral/launch → Veritral v2).
 * Uses auth token from apiService interceptor.
 */
export const launchVeritralGame = async (
  gameId: string,
  vendor?: string
): Promise<LaunchGameResponse> => {
  const response = await apiService.postRaw<LaunchGameResponse>('/api/veritral/launch', {
    gameId,
    vendor
  });
  if (response && response.status !== 0) {
    const msg =
      (response as any)?.message?.warning ||
      (response as any)?.message?.success ||
      'Failed to launch game';
    throw new Error(typeof msg === 'string' ? msg : 'Failed to launch game');
  }
  return response;
};

/**
 * Launch a demo game via Veritral (no login required)
 */
export const launchVeritralDemoGame = async (
  gameId: string,
  vendor?: string
): Promise<LaunchGameResponse> => {
  const response = await apiService.postRaw<LaunchGameResponse>('/api/veritral/demo', {
    gameId,
    vendor
  });
  return response;
};

/**
 * Sync games from Veritral to local database
 */
export const syncVeritralGames = async (): Promise<{ status: number; result?: { total: number; inserted: number; updated: number } }> => {
  const response = await apiService.postRaw<{ status: number; result?: { total: number; inserted: number; updated: number } }>('/api/veritral/sync', {});
  return response;
};
